/**
 * Login Form Validation Schema (Zod)
 *
 * Centralized validation schema for login form inputs.
 * Used with React Hook Form for runtime type-safe validation.
 */
import { z } from 'zod';
import { FIELD_LENGTHS, VALIDATION_MESSAGES } from '../constants/';

/**
 * Login Form Schema
 *
 * Validates:
 * - Company Code: Required, alphanumeric with _-, max 20 chars
 * - User ID: Required, alphanumeric with ._@-, max 50 chars
 * - Password: Required, max 100 chars
 * - Remember: Optional boolean
 */
export const loginFormSchema = z.object({
  companyCode: z
    .string()
    .min(1, VALIDATION_MESSAGES.COMPANY_CODE.REQUIRED)
    .max(FIELD_LENGTHS.COMPANY_CODE.MAX, VALIDATION_MESSAGES.COMPANY_CODE.MAX)
    .regex(/^[a-zA-Z0-9_-]+$/, VALIDATION_MESSAGES.COMPANY_CODE.INVALID),

  userId: z
    .string()
    .min(1, VALIDATION_MESSAGES.USER_ID.REQUIRED)
    .max(FIELD_LENGTHS.USER_ID.MAX, VALIDATION_MESSAGES.USER_ID.MAX)
    .regex(/^[a-zA-Z0-9._@-]+$/, VALIDATION_MESSAGES.USER_ID.INVALID),

  password: z
    .string()
    .min(1, VALIDATION_MESSAGES.PASSWORD.REQUIRED)
    .max(FIELD_LENGTHS.PASSWORD.MAX, VALIDATION_MESSAGES.PASSWORD.MAX),

  remember: z.boolean().optional(),
});

/**
 * TypeScript type inferred from Zod schema
 * Ensures type safety across the application
 */
export type LoginFormData = z.infer<typeof loginFormSchema>;

/**
 * Forgot Password Validation Schema
 * Only validates companyCode and userId fields
 */
export const forgotPasswordSchema = loginFormSchema.pick({
  companyCode: true,
  userId: true,
});

export type ForgotPasswordData = z.infer<typeof forgotPasswordSchema>;

/**
 * SSO Login Validation Schema
 * Only validates companyCode field for SSO authentication
 */
export const ssoLoginSchema = loginFormSchema.pick({
  companyCode: true,
});

export type SSOLoginData = z.infer<typeof ssoLoginSchema>;
